import { useEffect, useMemo, useState } from "react";
import {
    selectCrossBorderSchema,
    selectCrossBorderTransfer,
    setCrossBorderRecipientField,
    setCrossBorderRecipientMeta,
} from "../../redux/slices/crossBorderTransferSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ChooseBankModal from "./ChooseBankModal";
import { BiSolidBank } from "react-icons/bi";
import { MdArrowBackIos } from "react-icons/md";
import Input from "component/form/Input";

const clean = (v) => String(v ?? "").trim();

function validateIBANLoose(v) {
    const s = clean(v).replace(/\s+/g, "").toUpperCase();
    if (!s) return "";
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,32}$/.test(s)) return "IBAN looks invalid";
    return "";
}
function validateBICLoose(v) {
    const s = clean(v).toUpperCase();
    if (!s) return "";
    return /^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(s) ? "" : "SWIFT/BIC should be 8 or 11 characters";
}
function validateABARoutingLoose(v) {
    const s = clean(v).replace(/\D/g, "");
    if (!s) return "";
    return s.length === 9 ? "" : "Routing number should be 9 digits";
}
function validateUKSortCodeLoose(v) {
    const s = clean(v);
    if (!s) return "";
    const digits = s.replace(/\D/g, "");
    return digits.length === 6 ? "" : "Sort code should be 6 digits (e.g., 12-34-56)";
}
function validateCADTransitLoose(v) {
    const s = clean(v).replace(/\D/g, "");
    if (!s) return "";
    return s.length === 5 ? "" : "Transit number should be 5 digits";
}
function validateCADInstitutionLoose(v) {
    const s = clean(v).replace(/\D/g, "");
    if (!s) return "";
    return s.length === 3 ? "" : "Institution number should be 3 digits";
}
function validateDigitsRangeLoose(v, { min = 4, max = 34, label = "Value" } = {}) {
    const s = clean(v).replace(/\D/g, "");
    if (!s) return "";
    if (s.length < min || s.length > max) return `${label} should be ${min}–${max} digits`;
    return "";
}
function validateAlnumRangeLoose(v, { min = 4, max = 34, label = "Value" } = {}) {
    const s = clean(v).toUpperCase().replace(/\s/g, "");
    if (!s) return "";
    if (!/^[A-Z0-9]+$/.test(s)) return `${label} should contain only letters and digits`;
    if (s.length < min || s.length > max) return `${label} should be ${min}–${max} characters`;
    return "";
}

const formatSortCode = (raw) => {
    const d = String(raw).replace(/\D/g, "").slice(0, 6);
    return d.replace(/(\d{2})(\d{2})(\d{0,2})/, (_, a, b, c) =>
        c ? `${a}-${b}-${c}` : `${a}-${b}`
    );
};

function makeAccountValidatorLoose(currency) {
    const c = String(currency || "").toUpperCase();
    if (c === "NGN")
        return (v) => validateDigitsRangeLoose(v, { min: 10, max: 10, label: "Account number" });
    if (c === "USD")
        return (v) => validateDigitsRangeLoose(v, { min: 4, max: 17, label: "Account number" });
    return (v) => validateAlnumRangeLoose(v, { min: 4, max: 34, label: "Account number" });
}

function getValidatorsForCurrency(currency) {
    const acc = makeAccountValidatorLoose(currency);
    return {
        accountNumber: acc,
        iban: validateIBANLoose,
        swiftBic: validateBICLoose,
        routingNumber: validateABARoutingLoose,
        sortCode: validateUKSortCodeLoose,
        transitNumber: validateCADTransitLoose,
        institutionNumber: validateCADInstitutionLoose,
        bankCode: (v) => {
            const s = clean(v).replace(/\s/g, "");
            if (!s) return "";
            return /^[A-Za-z0-9\-]{2,}$/.test(s) ? "" : "Bank code looks too short";
        },
        branchCode: (v) => {
            const s = clean(v).replace(/\s/g, "");
            if (!s) return "";
            return /^[A-Za-z0-9\-]{2,}$/.test(s) ? "" : "Branch code looks too short";
        },
    };
}

export function CrossBorderRecipientFormGeneric({ onBack, nextRoute }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isCrossBorder = location.pathname.includes("cross-border");
    const clean = (v) => String(v ?? "").trim();

    const cb = useSelector(selectCrossBorderTransfer);
    const rawSchema = useSelector(selectCrossBorderSchema);
    const toCurrency = (cb?.recipient?.toCurrency || "").toUpperCase();
    const fromCurrency = (cb?.fromCurrency || "").toUpperCase();

    const { business } = useSelector((s) => s);
    const businessId = business?._id;

    const isKES = toCurrency === "KES";
    const isNGN = toCurrency === "NGN";
    const isGHS = toCurrency === "GHS";
    const isAutoCountries = isNGN;
    const isCAD = toCurrency === "CAD";
    const requiresBankPicker = !isCAD;

    const bankNameInStore = cb?.recipient?.bankName || "";

    const [fields, setFields] = useState(() => cb?.recipient?.fields || {});
    useEffect(() => setFields(cb?.recipient?.fields || {}), [cb?.recipient?.fields]);

    const schema = useMemo(() => {
        const base = rawSchema || [];
        if (isKES || isNGN || isGHS)
            return base.filter((f) => ["accountNumber", "accountName"].includes(f.key));
        return base;
    }, [rawSchema, isKES, isNGN, isGHS]);

    const validators = useMemo(() => getValidatorsForCurrency(toCurrency), [toCurrency]);

    const [showBankModal, setShowBankModal] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);

    const [loadingName, setLoadingName] = useState(false);
    const [nameStatus, setNameStatus] = useState("");

    const NUMERIC_KEYS = useMemo(
        () =>
            new Set([
                "routingNumber",
                "sortCode",
                "transitNumber",
                "institutionNumber",
                "accountNumber",
                "branchCode",
                "bankCode",
            ]),
        []
    );
    const UPPER_KEYS = useMemo(() => new Set(["swiftBic", "iban"]), []);

    const [errors, setErrors] = useState({});

    function validateAll(currentFields, currentSchema) {
        const next = {};
        (currentSchema || []).forEach((f) => {
            const v = currentFields[f.key];
            const isEmpty = clean(v) === "";
            if (f.required && isEmpty) next[f.key] = "This field is required";
            else if (!isEmpty && validators[f.key]) next[f.key] = validators[f.key](v);
            else next[f.key] = "";
        });
        setErrors(next);
        return Object.values(next).every((m) => !m);
    }

    useEffect(() => {
        if (schema?.length) validateAll(fields, schema);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema, toCurrency]);

    const onChange = (key) => (value) => {
        let v = value || "";
        if (key === "sortCode") v = formatSortCode(v);
        else if (NUMERIC_KEYS.has(key)) v = v.replace(/\D/g, "");
        else if (UPPER_KEYS.has(key)) v = v.toUpperCase().replace(/\s/g, "");

        const nextFields = { ...fields, [key]: v };
        setFields(nextFields);
        dispatch(setCrossBorderRecipientField({ key, value: v }));
        validateAll(nextFields, schema);
    };

    const handlePickBank = (bank) => {
        setSelectedBank(bank);
        dispatch(setCrossBorderRecipientMeta({ bankName: bank?.name || "" }));

        // if NGN/GHS/KES and you want bankCode stored:
        if (isNGN || isGHS || isKES) {
            dispatch(setCrossBorderRecipientField({ key: "bankCode", value: bank?.code || "" }));
            setFields((p) => ({ ...p, bankCode: bank?.code || "" }));
        }

        setShowBankModal(false);
    };

    // Auto-name resolution (NGN)
    useEffect(() => {
        if (!isCrossBorder || !isAutoCountries) return;

        const acct = String(fields.accountNumber || "").replace(/\D/g, "");
        if (acct.length < 10) {
            setNameStatus("");
            return;
        }
        const bankCode = selectedBank?.code || fields.bankCode || "";
        if (!bankCode) return;

        const controller = new AbortController();
        const run = async () => {
            setLoadingName(true);
            setNameStatus("validating");
            try {
                const payload = new URLSearchParams({
                    accountNumber: acct,
                    currency: fromCurrency,
                    currencyTo: toCurrency,
                    bankCode,
                });
                const res = await axios.post("/v1/wallets/verifyBankDetail", payload, {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "x-business-id": businessId,
                    },
                    signal: controller.signal,
                });
                const nm = res?.data?.data?.accountName || "";
                dispatch(setCrossBorderRecipientField({ key: "accountName", value: nm }));
                const nextFields = { ...fields, accountName: nm };
                setFields(nextFields);
                validateAll(nextFields, schema);
                setNameStatus(nm ? "" : "not-found");
            } catch (e) {
                if (e.name !== "CanceledError") setNameStatus("not-found");
            } finally {
                setLoadingName(false);
            }
        };
        const t = setTimeout(run, 600);
        return () => {
            clearTimeout(t);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCrossBorder, isAutoCountries, fields.accountNumber, selectedBank?.code, fields.bankCode]);

    const requiredOk = useMemo(() => {
        if (!isCrossBorder) return false;
        const metaOk = !requiresBankPicker || !!(selectedBank?.name || bankNameInStore);

        const schemaOk = (schema || []).every((f) => {
            const filled = !f.required || String(fields[f.key] || "").trim() !== "";
            const noError = !errors[f.key];
            return filled && noError;
        });

        const nameOk =
            !isAutoCountries ||
            (fields.accountName &&
                fields.accountName !== "Account not found" &&
                nameStatus !== "not-found");

        return metaOk && schemaOk && nameOk;
    }, [
        isCrossBorder,
        requiresBankPicker,
        selectedBank?.name,
        bankNameInStore,
        schema,
        fields,
        errors,
        isAutoCountries,
        nameStatus,
    ]);

    const renderField = (f) => {
        const val = fields[f.key] || "";
        const commonProps = {
            required: false,
            label: f.label,
            labelClassName: "!mt-0 text-[#101828]",
            className: "!mt-0",
            placeholder: f.label,
            value: val,
        };

        if (f.key === "accountName" && isAutoCountries) {
            return (
                <Input
                    {...commonProps}
                    disabled
                    value={loadingName ? "Validating..." : val || "No account selected"}
                    onChange={() => {}}
                />
            );
        }

        if (f.key === "sortCode") {
            return (
                <Input
                    {...commonProps}
                    inputMode="numeric"
                    pattern="[0-9\\-]*"
                    onChange={onChange(f.key)}
                />
            );
        }

        if (NUMERIC_KEYS.has(f.key)) {
            return (
                <Input
                    {...commonProps}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={onChange(f.key)}
                />
            );
        }

        return <Input {...commonProps} onChange={onChange(f.key)} />;
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-medium text-[#101828]">Add recipient</h1>

            {requiresBankPicker && (
                <div
                    className="flex items-center justify-between p-3 bg-[#FFF] rounded-lg cursor-pointer"
                    onClick={() => setShowBankModal(true)}
                >
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#F2F3FC] rounded-full flex items-center justify-center overflow-hidden">
                            <BiSolidBank size={22} />
                        </div>
                        <span className="text-[#344054] font-medium">
                            {selectedBank?.name || bankNameInStore || "Choose bank"}
                        </span>
                    </div>
                    <span className="text-[#667085] text-3xl font-thin">›</span>
                </div>
            )}

            {(schema || []).map((f) => (
                <div key={f.key}>
                    {renderField(f)}
                    {errors[f.key] && <p className="mt-1 text-sm text-red-600">{errors[f.key]}</p>}
                </div>
            ))}

            <div className="flex justify-between items-center mt-8">
                <button
                    className="text-[#101828] flex items-center gap-2"
                    onClick={() => onBack && onBack("Beneficiaries")}
                >
                    <MdArrowBackIos /> Select from beneficiaries
                </button>

                <div className="flex gap-8 items-center">
                    <button className="text-[#101828]" onClick={() => navigate(-1)}>
                        Back
                    </button>
                    <button
                        onClick={() => requiredOk && navigate(nextRoute)}
                        disabled={!requiredOk}
                        className={`rounded-lg px-6 py-2 font-semibold text-white transition ${
                            requiredOk
                                ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                        }`}
                    >
                        Confirm
                    </button>
                </div>
            </div>

            {requiresBankPicker && (
                <ChooseBankModal
                    isOpen={showBankModal}
                    onClose={() => setShowBankModal(false)}
                    onSelectBank={handlePickBank}
                />
            )}
        </div>
    );
}
